import { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import invariant from 'tiny-invariant';

import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import * as liveRegion from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/utils/reorder';
import type { Instruction } from '@atlaskit/side-nav-items/drag-and-drop/hitbox';
import type { ElementRegistry } from '@atlassian/navigation-drag-and-drop/element-registry';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { UserMenuDataShapes } from '../../common/types';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { messages } from './messages';

export interface DragAndDropAction {
	draggedMenuItemId: string;
	draggedMenuItemLabel?: string;
	droppedOnMenuItemId?: string;
	instruction: Instruction;
	trigger: 'pointer' | 'keyboard';
	type: 'item-reorder' | 'item-hide' | 'item-unhide' | 'item-visible';
}

type DragAndDropActionExtended =
	| ReorderDragAndDropAction
	| HideDragAndDropAction
	| UnhideDragAndDropAction
	| VisibleDragAndDropAction;

interface ReorderDragAndDropAction extends DragAndDropAction {
	finishIndex: number;
	startIndex: number;
	type: 'item-reorder';
}

interface HideDragAndDropAction extends DragAndDropAction {
	startIndex: number;
	type: 'item-hide';
	// finish is the More menu
}

interface UnhideDragAndDropAction extends DragAndDropAction {
	finishIndex: number;
	type: 'item-unhide';
	// start is the More menu
}

interface VisibleDragAndDropAction extends DragAndDropAction {
	type: 'item-visible';
}

export function useDragAndDropSave(
	userMenuDataShapes: UserMenuDataShapes,
	elementRegistry: ElementRegistry,
): {
	dragAndDropSave: (action: DragAndDropAction) => Promise<void>;
} {
	/*
	Get current order of items and shortcuts
	Apply reorder or hide operation
	Save the updated data using onSave
	*/
	const intl = useIntl();

	const { fireEvent } = useAnalyticsEvents();
	const { fireDraggedSideNavItemEvent } = useNavAnalytics();

	// Clean up live region when unmounted
	useEffect(() => {
		return () => liveRegion.cleanup();
	}, []);

	/**
	 * Run flash/focus/announce/analytics directly after
	 * state updates settle, without the intermediate state → effect chain.
	 * We use Promise.resolve().then() to schedule after React has flushed the state update.
	 */
	const runPostActionEffects = useCallback(
		(action: DragAndDropActionExtended) => {
			Promise.resolve().then(() => {
				if (isItemReorderAction(action)) {
					const label = action.draggedMenuItemLabel || action.draggedMenuItemId;
					const element = elementRegistry.getL1ItemElement(action.draggedMenuItemId);
					if (element) {
						triggerPostMoveFlash(element);
						if (action.trigger === 'keyboard') {
							setTimeout(() => element.focus());
						}
						liveRegion.announce(
							intl.formatMessage(messages.a11yAnnouncementItemReordered, {
								label,
								startIndex: action.startIndex + 1,
								finishIndex: action.finishIndex + 1,
							}),
						);
						fireDraggedSideNavItemEvent(action.draggedMenuItemId, false);
					}
					return;
				}

				if (isItemHideAction(action)) {
					const label = action.draggedMenuItemLabel || action.draggedMenuItemId;
					const moreMenuElement = elementRegistry.getMoreMenuElement();
					if (moreMenuElement) {
						triggerPostMoveFlash(moreMenuElement);
						if (action.trigger === 'keyboard') {
							setTimeout(() => moreMenuElement.focus());
						}
						liveRegion.announce(intl.formatMessage(messages.a11yAnnouncementItemHidden, { label }));
						fireDraggedSideNavItemEvent(action.draggedMenuItemId, true);
					}
					return;
				}

				if (isItemUnhideAction(action)) {
					const label = action.draggedMenuItemLabel || action.draggedMenuItemId;
					const element = elementRegistry.getL1ItemElement(action.draggedMenuItemId);
					if (element) {
						triggerPostMoveFlash(element);
						if (action.trigger === 'keyboard') {
							setTimeout(() => element.focus());
						}
						liveRegion.announce(
							intl.formatMessage(messages.a11yAnnouncementItemUnhidden, {
								label,
								finishIndex: action.finishIndex + 1,
							}),
						);
						fireDraggedSideNavItemEvent(action.draggedMenuItemId, false);
					}
					return;
				}

				if (isItemVisibleAction(action)) {
					const label = action.draggedMenuItemLabel || action.draggedMenuItemId;
					const moreMenuElement = elementRegistry.getMoreMenuElement();
					if (moreMenuElement) {
						liveRegion.announce(
							intl.formatMessage(messages.a11yAnnouncementItemVisible, { label }),
						);
						fireDraggedSideNavItemEvent(action.draggedMenuItemId, false);
					}
				}
			});
		},
		// Intentional omission of `fireDraggedSideNavItemEvent` from dependencies to avoid firing twice
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[elementRegistry, intl],
	);

	const dragAndDropSave = useCallback(
		async (action: DragAndDropAction): Promise<void> => {
			// item reordered into visible menu
			if (isItemReorderAction(action)) {
				invariant(action.droppedOnMenuItemId, 'item-reorder action must have droppedOnMenuItemId');

				const startIndex = userMenuDataShapes.userMenuData.findIndex(
					(datum) => datum.menuId === action.draggedMenuItemId,
				);
				const itemsAndShortcutsIdsWithoutDraggedItem = userMenuDataShapes.userMenuData
					.map((datum) => datum.menuId)
					.filter((id) => id !== action.draggedMenuItemId);
				const dropTargetIndex = itemsAndShortcutsIdsWithoutDraggedItem.indexOf(
					action.droppedOnMenuItemId,
				);

				if (startIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `startIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				if (dropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `dropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const finishIndex =
					action.instruction.operation === 'reorder-after' ? dropTargetIndex + 1 : dropTargetIndex;

				const visibleStartIndex = userMenuDataShapes.visibleMenuIds.indexOf(
					action.draggedMenuItemId,
				);
				const visibleDropTargetIndex = userMenuDataShapes.visibleMenuIds
					.filter((id) => id !== action.draggedMenuItemId)
					.indexOf(action.droppedOnMenuItemId);

				if (visibleStartIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `visibleStartIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				if (visibleDropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `visibleDropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const visibleFinishIndex =
					action.instruction.operation === 'reorder-after'
						? visibleDropTargetIndex + 1
						: visibleDropTargetIndex;

				const reordered = reorder({
					list: userMenuDataShapes.userMenuData,
					startIndex,
					finishIndex,
				});

				userMenuDataShapes.setUserMenuData(reordered);

				const extendedAction: DragAndDropActionExtended = {
					...action,
					startIndex: visibleStartIndex,
					finishIndex: visibleFinishIndex,
				};

				runPostActionEffects(extendedAction);
				return;
			}

			// item is dragged into hide button
			if (isItemHideAction(action)) {
				const dataWithHiddenItem = userMenuDataShapes.userMenuData.map((datum) => {
					if (datum.menuId === action.draggedMenuItemId) {
						return {
							...datum,
							visible: false,
						};
					}
					return datum;
				});

				userMenuDataShapes.setUserMenuData(dataWithHiddenItem);

				runPostActionEffects(action as DragAndDropActionExtended);
				return;
			}

			// hidden item dragged into visible button
			if (isItemVisibleAction(action)) {
				const dataWithVisibleItem = userMenuDataShapes.userMenuData.map((datum) => {
					if (datum.menuId === action.draggedMenuItemId) {
						return {
							...datum,
							visible: true,
						};
					}
					return datum;
				});

				userMenuDataShapes.setUserMenuData(dataWithVisibleItem);

				runPostActionEffects(action as DragAndDropActionExtended);

				return;
			}

			// hidden item dragged into visible menu
			if (isItemUnhideAction(action)) {
				invariant(action.droppedOnMenuItemId, 'item-unhide action must have droppedOnMenuItemId');
				const userMenuDataWithUnhiddenItem = userMenuDataShapes.userMenuData.map((data) => {
					if (data.menuId === action.draggedMenuItemId) {
						return {
							...data,
							visible: true,
						};
					}
					return data;
				});
				const startIndex = userMenuDataWithUnhiddenItem.findIndex(
					(datum) => datum.menuId === action.draggedMenuItemId,
				);
				const itemsAndShortcutsIdsWithoutDraggedItem = userMenuDataWithUnhiddenItem
					.map((datum) => datum.menuId)
					.filter((id) => id !== action.draggedMenuItemId);
				const dropTargetIndex = itemsAndShortcutsIdsWithoutDraggedItem.indexOf(
					action.droppedOnMenuItemId,
				);

				if (startIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `startIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				if (dropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `dropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const finishIndex =
					action.instruction.operation === 'reorder-after' ? dropTargetIndex + 1 : dropTargetIndex;

				const visibleDropTargetIndex = userMenuDataShapes.visibleMenuIds
					.filter((id) => id !== action.draggedMenuItemId)
					.indexOf(action.droppedOnMenuItemId);

				if (visibleDropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `visibleDropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const visibleFinishIndex =
					action.instruction.operation === 'reorder-after'
						? visibleDropTargetIndex + 1
						: visibleDropTargetIndex;

				const reordered = reorder({
					list: userMenuDataWithUnhiddenItem,
					startIndex,
					finishIndex,
				});

				userMenuDataShapes.setUserMenuData(reordered);

				const extendedAction: DragAndDropActionExtended = {
					...action,
					finishIndex: visibleFinishIndex,
				};

				runPostActionEffects(extendedAction);

				return;
			}
		},
		[fireEvent, runPostActionEffects, userMenuDataShapes],
	);

	return {
		dragAndDropSave,
	};
}

function isItemReorderAction(action: DragAndDropAction): action is ReorderDragAndDropAction {
	return action.type === 'item-reorder';
}

function isItemHideAction(action: DragAndDropAction): action is HideDragAndDropAction {
	return action.type === 'item-hide';
}

function isItemUnhideAction(action: DragAndDropAction): action is UnhideDragAndDropAction {
	return action.type === 'item-unhide';
}

function isItemVisibleAction(action: DragAndDropAction): action is VisibleDragAndDropAction {
	return action.type === 'item-visible';
}
